import * as React from 'react';
import styled from 'styled-components';
import { InputProps } from '@material-ui/core';
import { FieldError } from 'react-hook-form';
import ErrorMessages from './ErrorMessages';
import {
  MAX_UPLOAD_FILE_SIZE,
  MAX_UPLOAD_FILE_SIZE_LABEL,
} from '../constants';

const matchesAccept = (file: File, accept?: string): boolean => {
  if (!accept) return true;
  const entries = accept
    .split(',')
    .map(s => s.trim())
    .filter(Boolean);
  if (entries.length === 0) return true;
  const fileName = (file.name || '').toLowerCase();
  const fileType = file.type || '';
  const MIME_TO_EXT: Record<string, string> = {
    'application/pdf': '.pdf',
    'application/msword': '.doc',
    'text/plain': '.txt',
  };
  return entries.some(entry => {
    if (entry.startsWith('.')) {
      return fileName.endsWith(entry.toLowerCase());
    }
    if (entry.endsWith('/*')) {
      return fileType.startsWith(entry.slice(0, -1));
    }
    if (fileType === entry) return true;
    const ext = MIME_TO_EXT[entry];
    return !!ext && fileName.endsWith(ext);
  });
};

const tooLargeMessage = (label: string) =>
  `The file size is too large. Please upload a file below ${label}.`;

const acceptDescription = (accept?: string): string => {
  if (!accept) return 'these';
  const labels = accept
    .split(',')
    .map(s => s.trim())
    .filter(Boolean)
    .map(e => {
      if (e === 'application/pdf' || e === '.pdf') return 'PDF';
      if (e === 'application/msword' || e === '.doc') return 'DOC';
      if (e === 'text/plain' || e === '.txt') return 'TXT';
      if (e.startsWith('.')) return e.slice(1).toUpperCase();
      if (e.endsWith('/*')) return e.split('/')[0].toUpperCase();
      return e;
    });
  return Array.from(new Set(labels)).join(' / ');
};

const InputFileContainer = ({
  error,
  isRequired,
  accept,
  disabled,
  multiple,
  onChange,
  hideFileList,
  maxSize = MAX_UPLOAD_FILE_SIZE,
  maxSizeLabel = MAX_UPLOAD_FILE_SIZE_LABEL,
  ...props
}: InputFileContainerProps) => {
  const [fileNames, setFileNames] = React.useState<string[]>([]);
  const [acceptError, setAcceptError] = React.useState<string>('');

  // Reject oversized files here so the request is never sent — otherwise the
  // server answers 413 and the user sees a raw technical error.
  const isTooLarge = (file: File) => !!maxSize && file.size > maxSize;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files?.length) {
      const rejected = Array.from(files).some(f => !matchesAccept(f, accept));
      if (rejected) {
        setAcceptError(`Only ${acceptDescription(accept)} files are allowed.`);
        e.target.value = '';
        return;
      }
      if (Array.from(files).some(isTooLarge)) {
        setAcceptError(tooLargeMessage(maxSizeLabel));
        e.target.value = '';
        return;
      }
      setAcceptError('');
      setFileNames(Array.from(files).map(f => f.name));
    } else {
      setFileNames([]);
    }
    if (onChange) {
      onChange(e as any);
    }
  };

  return (
    <Wrapper>
      <DropArea
        $error={!!error}
        $disabled={!!disabled}
        onDragOver={e => { e.preventDefault(); e.currentTarget.setAttribute('data-drag', 'true'); }}
        onDragLeave={e => { e.currentTarget.removeAttribute('data-drag'); }}
        onDrop={e => {
          e.preventDefault();
          e.currentTarget.removeAttribute('data-drag');
          if (!disabled && e.dataTransfer.files?.length) {
            const dropped = Array.from(e.dataTransfer.files);
            const rejected = dropped.some(f => !matchesAccept(f, accept));
            if (rejected) {
              setAcceptError(`Only ${acceptDescription(accept)} files are allowed.`);
              return;
            }
            if (dropped.some(isTooLarge)) {
              setAcceptError(tooLargeMessage(maxSizeLabel));
              return;
            }
            setAcceptError('');
            const dt = new DataTransfer();
            dropped.forEach(f => dt.items.add(f));
            setFileNames(dropped.map(f => f.name));
            if (onChange) {
              onChange({ target: { files: dt.files } } as any);
            }
          }
        }}
      >
        <UploadIcon>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
            <polyline points="17 8 12 3 7 8" />
            <line x1="12" y1="3" x2="12" y2="15" />
          </svg>
        </UploadIcon>
        <DropText>
          Drop or <BrowseLabel>browse<HiddenInput type="file" accept={accept} multiple={multiple} onChange={handleChange} disabled={disabled} /></BrowseLabel>{accept ? ` ${acceptDescription(accept)} files here` : ' files here'}
        </DropText>
      </DropArea>
      {!hideFileList && fileNames.length > 0 && (
        <FileList>
          {fileNames.map((name, i) => (
            <FileChip key={i}>{name}</FileChip>
          ))}
        </FileList>
      )}
      {acceptError && <AcceptError>{acceptError}</AcceptError>}
      {error && <ErrorMessages error={error} />}
    </Wrapper>
  );
};

const Wrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
`;

const DropArea = styled.div<{ $error: boolean; $disabled: boolean }>`
  display: flex;
  align-items: center;
  gap: var(--space-3);
  padding: var(--space-3) var(--space-4);
  border: 2px dashed ${({ $error }) => $error ? 'var(--danger-500)' : 'var(--neutral-300)'};
  border-radius: var(--radius-md);
  background: var(--neutral-50);
  cursor: ${({ $disabled }) => $disabled ? 'not-allowed' : 'pointer'};
  opacity: ${({ $disabled }) => $disabled ? 0.5 : 1};
  transition: border-color var(--motion-fast) var(--ease-standard),
    background-color var(--motion-fast) var(--ease-standard);

  &[data-drag='true'] {
    border-color: var(--primary-600);
    background: var(--primary-50);
  }

  &:hover {
    border-color: ${({ $disabled }) => $disabled ? '' : 'var(--primary-400)'};
  }
`;

const UploadIcon = styled.div`
  color: var(--neutral-400);
  flex-shrink: 0;
`;

const DropText = styled.div`
  font-size: var(--fs-sm);
  color: var(--neutral-600);
`;

const BrowseLabel = styled.label`
  color: var(--primary-600);
  font-weight: var(--fw-semibold);
  cursor: pointer;
  text-decoration: underline;
  text-underline-offset: 2px;

  &:hover { color: var(--primary-700); }
`;

const HiddenInput = styled.input`
  display: none;
`;

const FileList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-2);
`;

const AcceptError = styled.div`
  font-size: var(--fs-xs);
  color: var(--danger-500);
`;

const FileChip = styled.div`
  display: inline-flex;
  align-items: center;
  padding: var(--space-1) var(--space-3);
  background: var(--primary-50);
  border: 1px solid var(--primary-200);
  border-radius: var(--radius-full);
  font-size: var(--fs-sm);
  color: var(--primary-800);
`;

type InputFileContainerProps = Omit<
  InputProps,
  'error' | 'variant'
> & {
  isRequired?: boolean;
  error?: FieldError;
  accept?: string;
  multiple?: boolean;
  hideFileList?: boolean;
  maxSize?: number;
  maxSizeLabel?: string;
};

export default InputFileContainer;
